/**
 * Self-service PayMe card-update modal.
 *
 * Opening the modal first shows a short notice ("you must update your card");
 * the customer then clicks "update credit card" to load the PayMe Hosted
 * Fields session inside an iframe, so card data never touches the storefront.
 * The Settings tab / ICOUNT banner button (data-action="open-card-update")
 * and the subscription-action gate both open the modal at this notice step.
 *
 * On a successful tokenization, PayMe posts a message to the parent window
 * (or redirects the iframe to a Laravel return URL); we close the modal,
 * re-fetch the /me snapshot so the ICOUNT banner clears, and notify any
 * waiting onSuccess callback so ICOUNT-blocked operations can retry.
 *
 * The admin preview may start a card update too — it is the one write preview allows,
 * because it can only ever unblock a subscription, never change a plan.
 *
 * Depends on: dog-dashboard-api.js (window.MillsDogDashboard.api), helpers.
 */
(function (w, d) {
  "use strict";

  var M = w.MillsDogDashboard = w.MillsDogDashboard || {};

  var MODAL_ID = "mills-card-update-modal";
  var IFRAME_ID = "mills-card-update-iframe";
  var SUCCESS_MESSAGE = "פרטי התשלום עודכנו";
  var ERROR_MESSAGE_FALLBACK = "עדכון אמצעי התשלום נכשל. נסה שוב.";
  var NOTICE_MESSAGE = "כדי לבצע שינויים במנוי יש לעדכן את אמצעי התשלום. המנוי מחויב כעת דרך מערכת חיוב ישנה.";

  function buildModalRoot() {
    var existing = d.getElementById(MODAL_ID);
    if (existing) return existing;

    var overlay = d.createElement("div");
    overlay.id = MODAL_ID;
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");
    overlay.setAttribute("aria-label", "עדכון אמצעי תשלום");
    overlay.style.cssText = [
      "position:fixed",
      "inset:0",
      "z-index:99999",
      "background:rgba(0,0,0,0.6)",
      "display:flex",
      "align-items:center",
      "justify-content:center",
      "padding:16px",
    ].join(";");

    overlay.innerHTML =
      '<div class="mills-card-update-dialog" style="background:#fff;max-width:520px;width:100%;border-radius:8px;overflow:hidden;display:flex;flex-direction:column;max-height:90vh;">' +
      '  <div style="display:flex;align-items:center;justify-content:space-between;padding:14px 18px;border-bottom:1px solid #eee;">' +
      '    <strong style="font-size:1.05rem;">עדכון כרטיס אשראי</strong>' +
      '    <button type="button" class="mills-card-update-close" aria-label="סגירה" style="background:transparent;border:0;font-size:1.4rem;cursor:pointer;">×</button>' +
      '  </div>' +
      '  <div class="mills-card-update-body" style="flex:1;min-height:360px;position:relative;">' +
      '    <div class="mills-card-update-notice" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:20px;padding:28px 24px;text-align:center;direction:rtl;">' +
      '      <p class="mills-card-update-notice-text" style="margin:0;color:#333;line-height:1.6;font-size:1rem;">' + NOTICE_MESSAGE + '</p>' +
      '      <button type="button" class="mills-card-update-proceed" style="background:#0b2b4a;color:#fff;border:0;border-radius:8px;padding:11px 26px;font-size:1rem;cursor:pointer;">עדכן כרטיס אשראי</button>' +
      '    </div>' +
      '    <div class="mills-card-update-status" style="position:absolute;inset:0;display:none;align-items:center;justify-content:center;color:#666;padding:24px;text-align:center;">טוען...</div>' +
      '    <iframe id="' + IFRAME_ID + '" style="display:none;width:100%;height:100%;border:0;min-height:360px;" allow="payment *"></iframe>' +
      '  </div>' +
      '</div>';

    d.body.appendChild(overlay);
    return overlay;
  }

  function showError(modal, message) {
    var status = modal.querySelector(".mills-card-update-status");
    var iframe = modal.querySelector("#" + IFRAME_ID);
    if (iframe) iframe.style.display = "none";
    if (status) {
      status.style.display = "flex";
      status.style.color = "#b00";
      status.textContent = message || ERROR_MESSAGE_FALLBACK;
    }
  }

  function loadIframe(modal, hostedUrl) {
    var iframe = modal.querySelector("#" + IFRAME_ID);
    var status = modal.querySelector(".mills-card-update-status");
    if (!iframe) return;
    iframe.src = hostedUrl;
    iframe.style.display = "block";
    if (status) status.style.display = "none";
  }

  function closeModal(modal) {
    if (!modal) return;
    modal.remove();
    if (typeof state.onClose === "function") {
      try { state.onClose(); } catch (_e) { /* ignore */ }
    }
  }

  function wireCloseHandlers(modal) {
    modal.querySelector(".mills-card-update-close").addEventListener("click", function () {
      closeModal(modal);
    });
    modal.addEventListener("click", function (e) {
      if (e.target === modal) closeModal(modal);
    });
  }

  /*
   * Step 2: the customer chose to proceed — including from the admin preview.
   *
   * Preview is read-only for everything EXCEPT this. Updating a card cannot alter anyone's
   * plan: it opens PayMe's own page, the customer types the card there, and the only outcome
   * is that a blocked subscription becomes billable again. Blocking it meant the one thing
   * support is on the phone to help with was the one thing the preview could not do. The
   * server allows the same single endpoint and nothing else.
   */
  function proceedToUpdate(modal) {
    var notice = modal.querySelector(".mills-card-update-notice");
    var status = modal.querySelector(".mills-card-update-status");
    if (notice) notice.style.display = "none";
    if (status) {
      status.style.display = "flex";
      status.style.color = "#666";
      status.textContent = "טוען...";
    }
    createSessionAndOpenIframe(modal);
  }

  function wireProceed(modal) {
    var btn = modal.querySelector(".mills-card-update-proceed");
    if (btn) {
      btn.addEventListener("click", function () { proceedToUpdate(modal); });
    }
  }

  // Re-fetch /me and re-run hydration so the ICOUNT banner and integration
  // status reflect the just-completed card update without a page reload.
  // Returns a Promise so handleSuccessMessage can await the new payload
  // before closing the modal (Phase 2.44).
  function refreshMeSnapshot() {
    if (w.MillsHydrate && typeof w.MillsHydrate.refresh === "function") {
      try { return w.MillsHydrate.refresh(); } catch (_e) { return Promise.resolve(); }
    }
    if (w.MillsHydrate && typeof w.MillsHydrate.invalidateCache === "function") {
      w.MillsHydrate.invalidateCache();
    }
    return Promise.resolve();
  }

  // Swap the iframe/notice for a "refreshing..." status while we wait for the
  // post-flip /me to re-render. Safe if the modal is already gone (race).
  function showRefreshingState(modal) {
    if (!modal) return;
    var notice = modal.querySelector(".mills-card-update-notice");
    var iframe = modal.querySelector("#" + IFRAME_ID);
    var status = modal.querySelector(".mills-card-update-status");
    if (notice) notice.style.display = "none";
    if (iframe) iframe.style.display = "none";
    if (status) {
      status.style.display = "flex";
      status.style.color = "#666";
      status.textContent = "מעדכן נתונים...";
    }
  }

  function showToastIfAvailable(message, ok) {
    var t = w.MillsDogDashboard && w.MillsDogDashboard.showToast;
    if (typeof t === "function") {
      try { t(message, ok); } catch (_e) { /* ignore */ }
    }
  }

  function readMeta(name) {
    var el = d.querySelector('meta[name="' + name + '"]');
    return el ? (el.getAttribute("content") || "").trim() : "";
  }

  function storefrontBase() {
    if (w.MillsAuth) return w.MillsAuth.base();
    var base = readMeta("mills-storefront-api-base")
      || "https://mills.lets.co.il/storefront";
    return base.replace(/\/$/, "");
  }

  function buildAuthHeaders() {
    var headers = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };
    var token = w.MillsAuth ? w.MillsAuth.token() : readMeta("mills-storefront-token");
    if (token) headers["Authorization"] = "Bearer " + token;
    return headers;
  }

  var state = { onSuccess: null, onClose: null, subscriptionId: null };

  async function createSessionAndOpenIframe(modal) {
    try {
      var body = {};
      if (state.subscriptionId) body.subscription_id = state.subscriptionId;
      var res = await fetch(storefrontBase() + "/me/payment-method/payme/session", {
        method: "POST",
        mode: "cors",
        credentials: "omit",
        headers: buildAuthHeaders(),
        body: JSON.stringify(body),
      });
      var data = {};
      try { data = await res.json(); } catch (_e) { data = {}; }

      if (!res.ok || !data.ok) {
        var msg = (data && (data.message || data.error)) || ERROR_MESSAGE_FALLBACK;
        showError(modal, msg);
        return;
      }

      var hosted = data.data && data.data.hosted_url;
      if (!hosted) {
        showError(modal, "לא התקבל קישור לתשלום מהשרת.");
        return;
      }

      loadIframe(modal, hosted);
    } catch (err) {
      console.warn("[card-update] session create failed", err);
      showError(modal, ERROR_MESSAGE_FALLBACK);
    }
  }

  // Trust only messages from the Laravel-hosted card-update page (same origin
  // as the storefront API). Without this, any iframe or inline script on the
  // page can spoof `mills.payme.success` and bypass the ICOUNT card-update gate.
  function expectedMessageOrigin() {
    try { return new URL(storefrontBase()).origin; } catch (_e) { return ""; }
  }

  // Single, module-lifetime listener (registered once at the bottom of this
  // file). Survives modal open/close cycles so a PayMe success message that
  // arrives milliseconds after the user dismisses the modal is still applied
  // (refresh /me, fire onSuccess, close any still-open modal).
  // Awaits the /me refresh so the dashboard already shows the post-flip
  // payload (real PayMe subscription, no ICOUNT banner) by the time we close
  // the modal. Capped at 5s to never trap a customer on a hung backend.
  async function handleSuccessMessage(event) {
    var expected = expectedMessageOrigin();
    if (expected && event && event.origin !== expected) return;
    var data = event && event.data;
    if (!data || typeof data !== "object") return;
    if (data.type !== "mills.payme.success" && data.type !== "payme.success") return;

    var modal = d.getElementById(MODAL_ID);
    showRefreshingState(modal);

    try {
      await Promise.race([
        refreshMeSnapshot(),
        new Promise(function (resolve) { w.setTimeout(resolve, 5000); }),
      ]);
    } catch (_e) { /* swallow — we still close the modal */ }

    showToastIfAvailable(SUCCESS_MESSAGE, true);
    var cb = state.onSuccess;
    state.onSuccess = null;
    // Re-read in case the customer dismissed the modal during the wait.
    var modalNow = d.getElementById(MODAL_ID);
    if (modalNow) closeModal(modalNow);
    if (typeof cb === "function") {
      try { cb(data); } catch (_e) { /* ignore */ }
    }
  }

  /** Opens the card-update modal at its notice step. opts: { subscriptionId, onSuccess, onClose }. */
  M.openCardUpdateModal = function (opts) {
    var o = opts || {};
    state.onSuccess = typeof o.onSuccess === "function" ? o.onSuccess : null;
    state.onClose = typeof o.onClose === "function" ? o.onClose : null;
    state.subscriptionId = o.subscriptionId || null;

    var modal = buildModalRoot();
    wireCloseHandlers(modal);
    wireProceed(modal);

    // Stage 1 (notice) is rendered by buildModalRoot; the PayMe form loads
    // only when the customer clicks the proceed button. The success listener
    // is registered globally — no per-open wiring needed.
  };

  w.addEventListener("message", handleSuccessMessage);

  // Wire the Settings tab / ICOUNT banner button (data-action="open-card-update")
  d.addEventListener("click", function (e) {
    var btn = e.target.closest && e.target.closest('[data-action="open-card-update"]');
    if (!btn) return;
    e.preventDefault();
    M.openCardUpdateModal({});
  });
})(window, document);
